import { Button } from '@/components/ui/button';
import { Heart, Settings, AlertCircle } from 'lucide-react';
import { openNativeSettings, isDespiaNative } from '@/utils/despia';
import { toast } from 'sonner';

interface HealthPermissionDeniedScreenProps {
  platform: 'apple' | 'android' | 'none';
  onDismiss?: () => void;
}

/**
 * Post-Denial Screen for HealthKit Permission
 * 
 * Apple Guidelines Compliance:
 * Shows only AFTER the user has denied the system HealthKit permission dialog.
 * Provides a single "Open Settings" button to deep-link to the app's Settings page.
 */
export function HealthPermissionDeniedScreen({ 
  platform, 
  onDismiss 
}: HealthPermissionDeniedScreenProps) {
  const isApple = platform === 'apple';
  const platformName = isApple ? 'Apple Health' : 'Health Connect';

  const handleOpenSettings = () => {
    console.log('[HealthDenied] Opening settings...');
    
    // Try to open native settings - works in native app environment
    openNativeSettings();
    
    // Show a toast to guide user in case deep link doesn't work
    toast.info(
      isApple 
        ? 'Go to Settings → Privacy & Security → Health → Evolve' 
        : 'Go to Settings → Apps → Health Connect → Evolve',
      { duration: 5000 }
    );
  };

  return (
    <div className="flex flex-col items-center justify-center p-6 text-center space-y-6">
      {/* Icon */}
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
        isApple ? 'bg-red-500/10' : 'bg-green-500/10'
      }`}>
        <Heart className={`w-10 h-10 ${isApple ? 'text-red-500' : 'text-green-500'}`} />
      </div>

      {/* Title */}
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Health Access Required</h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          To sync your fitness data with {platformName}, please enable Health access in your device settings.
        </p>
      </div>

      {/* Instructions */}
      <div className="p-4 rounded-lg bg-muted/30 text-left w-full max-w-sm">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium">How to enable:</p>
            {isApple ? (
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Tap "Open Settings" below</li>
                <li>Select "Health"</li>
                <li>Turn on the permissions you'd like to share</li>
              </ol>
            ) : (
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Tap "Open Settings" below</li>
                <li>Select "Permissions" → "Health Connect"</li>
                <li>Enable the data types you'd like to sync</li>
              </ol>
            )}
          </div>
        </div>
      </div>

      {/* Open Settings Button */}
      <Button
        onClick={handleOpenSettings}
        className={`w-full max-w-sm ${isApple ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}`}
      >
        <Settings className="w-4 h-4 mr-2" />
        Open Settings
      </Button>

      {/* Dismiss option */}
      {onDismiss && (
        <Button
          variant="ghost"
          onClick={onDismiss}
          className="text-muted-foreground"
        >
          Maybe Later
        </Button>
      )}
    </div>
  );
}
